import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../../axiosConfig';
import { TrainerContext } from '../../context/TrainerContext';
import '../../styles/BillingPreferenceModal.css';

/**
 * BillingPreferenceModal Component
 *
 * Modal shown before purchasing a paid subscription to choose billing preference:
 * - With Invoice (Faktura) - requires invoice/company data
 * - Without Invoice - uses personal data (name + address)
 */
const BillingPreferenceModal = ({
    isOpen,
    onClose,
    onConfirm,
    planName,
    planPrice
}) => {
    const { t } = useTranslation(['subscription', 'common']);

    // Safely access TrainerContext (may not be available on public routes)
    const trainerContext = useContext(TrainerContext);
    const trainer = trainerContext?.trainer;
    const schoolId = trainer?.schoolId;

    // Trainer data loading state (fetch if not available from context)
    const [trainerData, setTrainerData] = useState(null);
    const [trainerLoading, setTrainerLoading] = useState(false);

    // Billing preference: 'invoice' or 'personal'
    const [billingType, setBillingType] = useState('personal');

    // Invoice data state
    const [invoiceData, setInvoiceData] = useState(null);
    const [invoiceLoading, setInvoiceLoading] = useState(false);
    const [invoiceEditMode, setInvoiceEditMode] = useState(false);
    const [invoiceFormData, setInvoiceFormData] = useState({
        countryCode: 'PL',
        companyName: '',
        nip: '',
        regon: '',
        street: '',
        buildingNumber: '',
        apartmentNumber: '',
        postalCode: '',
        city: '',
        country: 'Polska',
    });

    // Personal data confirmation
    const [personalDataConfirmed, setPersonalDataConfirmed] = useState(false);

    // Error state
    const [error, setError] = useState('');

    // Effective trainer data (from context or fetched)
    const effectiveTrainer = trainer || trainerData;
    const effectiveSchoolId = effectiveTrainer?.schoolId || schoolId;

    // Fetch trainer data if not available from context
    useEffect(() => {
        if (isOpen && !trainer && !trainerData && !trainerLoading) {
            fetchTrainerData();
        }
    }, [isOpen, trainer, trainerData, trainerLoading]);

    const fetchTrainerData = async () => {
        setTrainerLoading(true);
        try {
            const token = localStorage.getItem('jwt');
            if (!token) return;

            const response = await axios.get('/api/v1/trainer/trainer-details', {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (response.data) {
                setTrainerData(response.data);
            }
        } catch (err) {
            console.error('Error fetching trainer data:', err);
        } finally {
            setTrainerLoading(false);
        }
    };

    // Fetch invoice data when modal opens and invoice tab is selected
    useEffect(() => {
        if (isOpen && billingType === 'invoice' && effectiveSchoolId && !invoiceData && !invoiceLoading) {
            fetchInvoiceData();
        }
    }, [isOpen, billingType, effectiveSchoolId]);

    const fetchInvoiceData = async () => {
        if (!effectiveSchoolId) return;

        setInvoiceLoading(true);
        setError('');

        try {
            const token = localStorage.getItem('jwt');
            const response = await axios.get(`/api/v1/school/${effectiveSchoolId}/invoice-data`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (response.status === 200 && response.data) {
                setInvoiceData(response.data);
                setInvoiceFormData(response.data);
                setInvoiceEditMode(false);
            }
        } catch (err) {
            // 204 No Content means no invoice data exists
            if (err.response?.status !== 204) {
                console.error('Error fetching invoice data:', err);
            }
            setInvoiceData(null);
            setInvoiceEditMode(true); // Enable form for adding new data
        } finally {
            setInvoiceLoading(false);
        }
    };

    const handleInvoiceChange = (e) => {
        const { name, value } = e.target;
        setInvoiceFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const handleSaveInvoiceData = async () => {
        if (!effectiveSchoolId) return;

        // Validate required fields
        if (!invoiceFormData.companyName?.trim()) {
            setError(t('billing.companyNameRequired'));
            return;
        }
        if (!invoiceFormData.street?.trim()) {
            setError(t('billing.streetRequired'));
            return;
        }
        if (!invoiceFormData.postalCode?.trim()) {
            setError(t('billing.postalCodeRequired'));
            return;
        }
        if (!invoiceFormData.city?.trim()) {
            setError(t('billing.cityRequired'));
            return;
        }
        if (!invoiceFormData.country?.trim()) {
            setError(t('billing.countryRequired'));
            return;
        }

        setError('');

        try {
            const token = localStorage.getItem('jwt');
            const response = await axios.put(
                `/api/v1/school/${effectiveSchoolId}/invoice-data`,
                invoiceFormData,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setInvoiceData(response.data);
            setInvoiceFormData(response.data);
            setInvoiceEditMode(false);
        } catch (err) {
            setError(t('billing.invoiceSaveError'));
            console.error('Error saving invoice data:', err);
        }
    };

    const handleConfirm = useCallback(() => {
        if (billingType === 'invoice') {
            if (!invoiceData) {
                setError(t('billing.addInvoiceBeforeContinue'));
                return;
            }
            onConfirm({
                billingType: 'invoice',
                invoiceData: invoiceData
            });
        } else {
            if (!personalDataConfirmed) {
                setError(t('billing.confirmPersonalData'));
                return;
            }
            onConfirm({
                billingType: 'personal',
                personalData: {
                    firstName: effectiveTrainer?.firstName,
                    lastName: effectiveTrainer?.lastName,
                    address: effectiveTrainer?.address
                }
            });
        }
    }, [billingType, invoiceData, personalDataConfirmed, effectiveTrainer, onConfirm]);

    const formatAddress = (address) => {
        if (!address) return t('billing.noAddress');
        const parts = [];
        if (address.street) {
            let streetLine = address.street;
            if (address.buildingNumber) streetLine += ` ${address.buildingNumber}`;
            if (address.apartmentNumber) streetLine += `/${address.apartmentNumber}`;
            parts.push(streetLine);
        }
        if (address.postalCode || address.city) {
            parts.push(`${address.postalCode || ''} ${address.city || ''}`.trim());
        }
        if (address.country) parts.push(address.country);
        return parts.join(', ') || t('billing.noAddress');
    };

    if (!isOpen) return null;

    return (
        <div className="billing-modal-overlay" onClick={onClose}>
            <div className="billing-modal" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="billing-modal__header">
                    <h2>{t('billing.chooseBillingMethod')}</h2>
                    <p className="billing-modal__plan-info">
                        {t('billing.planInfo')} <strong>{planName}</strong> - {planPrice}
                    </p>
                    <button className="billing-modal__close" onClick={onClose}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                        </svg>
                    </button>
                </div>

                {/* Billing Type Selector */}
                <div className="billing-modal__tabs">
                    <button
                        className={`billing-modal__tab ${billingType === 'personal' ? 'billing-modal__tab--active' : ''}`}
                        onClick={() => setBillingType('personal')}
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                        </svg>
                        {t('billing.withoutInvoice')}
                    </button>
                    <button
                        className={`billing-modal__tab ${billingType === 'invoice' ? 'billing-modal__tab--active' : ''}`}
                        onClick={() => setBillingType('invoice')}
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm4 18H6V4h7v5h5v11zM8 15.01V17h2v-1.99H8zm0-4V13h2v-1.99H8zm4 4V17h5v-1.99h-5zm0-4V13h5v-1.99h-5z"/>
                        </svg>
                        {t('billing.withInvoice')}
                    </button>
                </div>

                {/* Error Banner */}
                {error && (
                    <div className="billing-modal__error">
                        <span>{error}</span>
                        <button onClick={() => setError('')}>×</button>
                    </div>
                )}

                {/* Content */}
                <div className="billing-modal__content">
                    {billingType === 'personal' ? (
                        trainerLoading ? (
                            <div className="billing-modal__section">
                                <div className="billing-modal__loading">
                                    <div className="billing-modal__spinner"></div>
                                    <p>{t('billing.loadingData')}</p>
                                </div>
                            </div>
                        ) : (
                            <PersonalDataSection
                                trainer={effectiveTrainer}
                                formatAddress={formatAddress}
                                confirmed={personalDataConfirmed}
                                onConfirmChange={setPersonalDataConfirmed}
                            />
                        )
                    ) : (
                        <InvoiceDataSection
                            invoiceData={invoiceData}
                            invoiceFormData={invoiceFormData}
                            invoiceLoading={invoiceLoading}
                            invoiceEditMode={invoiceEditMode}
                            onInvoiceChange={handleInvoiceChange}
                            onSave={handleSaveInvoiceData}
                            onEdit={() => setInvoiceEditMode(true)}
                            onCancel={() => {
                                if (invoiceData) {
                                    setInvoiceFormData(invoiceData);
                                    setInvoiceEditMode(false);
                                }
                            }}
                            formatAddress={formatAddress}
                        />
                    )}
                </div>

                {/* Actions */}
                <div className="billing-modal__actions">
                    <button
                        className="billing-modal__btn billing-modal__btn--secondary"
                        onClick={onClose}
                    >
                        {t('common:cancel')}
                    </button>
                    <button
                        className="billing-modal__btn billing-modal__btn--primary"
                        onClick={handleConfirm}
                        disabled={
                            (billingType === 'invoice' && (!invoiceData || invoiceEditMode)) ||
                            (billingType === 'personal' && !personalDataConfirmed)
                        }
                    >
                        {t('billing.proceedToPayment')}
                    </button>
                </div>
            </div>
        </div>
    );
};

/**
 * Personal Data Section - shown when billing without invoice
 */
const PersonalDataSection = ({ trainer, formatAddress, confirmed, onConfirmChange }) => {
    const { t } = useTranslation(['subscription', 'common']);
    return (
        <div className="billing-modal__section">
            <h3 className="billing-modal__section-title">{t('billing.billingData')}</h3>
            <p className="billing-modal__section-desc">
                {t('billing.billingDataDesc')}
            </p>

            <div className="billing-modal__data-card">
                <div className="billing-modal__data-row">
                    <span className="billing-modal__data-label">{t('billing.fullName')}</span>
                    <span className="billing-modal__data-value">
                        {trainer?.firstName || ''} {trainer?.lastName || ''}
                    </span>
                </div>
                <div className="billing-modal__data-row">
                    <span className="billing-modal__data-label">{t('billing.address')}</span>
                    <span className="billing-modal__data-value">
                        {formatAddress(trainer?.address)}
                    </span>
                </div>
                <div className="billing-modal__data-row">
                    <span className="billing-modal__data-label">Email</span>
                    <span className="billing-modal__data-value">
                        {trainer?.email || t('billing.noEmail')}
                    </span>
                </div>
            </div>

            <label className="billing-modal__checkbox-label">
                <input
                    type="checkbox"
                    checked={confirmed}
                    onChange={(e) => onConfirmChange(e.target.checked)}
                    className="billing-modal__checkbox"
                />
                <span className="billing-modal__checkbox-text">
                    {t('billing.confirmDataCorrect')}
                </span>
            </label>

            <p className="billing-modal__hint">
                {t('billing.dataIncorrectHint')}
            </p>
        </div>
    );
};

/**
 * Invoice Data Section - shown when billing with invoice
 */
const InvoiceDataSection = ({
    invoiceData,
    invoiceFormData,
    invoiceLoading,
    invoiceEditMode,
    onInvoiceChange,
    onSave,
    onEdit,
    onCancel,
    formatAddress
}) => {
    const { t } = useTranslation(['subscription', 'common']);

    if (invoiceLoading) {
        return (
            <div className="billing-modal__section">
                <div className="billing-modal__loading">
                    <div className="billing-modal__spinner"></div>
                    <p>{t('billing.loadingInvoiceData')}</p>
                </div>
            </div>
        );
    }

    // No data exists - show form
    if (!invoiceData || invoiceEditMode) {
        return (
            <div className="billing-modal__section">
                <h3 className="billing-modal__section-title">
                    {invoiceData ? t('billing.editInvoiceData') : t('billing.addInvoiceData')}
                </h3>
                <p className="billing-modal__section-desc">
                    {t('billing.invoiceFormDesc')}
                </p>

                <div className="billing-modal__form">
                    {/* Company Info */}
                    <div className="billing-modal__form-group">
                        <label className="billing-modal__form-label">{t('billing.companyName')} *</label>
                        <input
                            type="text"
                            name="companyName"
                            value={invoiceFormData.companyName || ''}
                            onChange={onInvoiceChange}
                            placeholder={t('billing.companyName')}
                            className="billing-modal__form-input"
                        />
                    </div>

                    <div className="billing-modal__form-row">
                        <div className="billing-modal__form-group">
                            <label className="billing-modal__form-label">NIP</label>
                            <input
                                type="text"
                                name="nip"
                                value={invoiceFormData.nip || ''}
                                onChange={onInvoiceChange}
                                placeholder="0000000000"
                                className="billing-modal__form-input"
                            />
                        </div>
                        <div className="billing-modal__form-group">
                            <label className="billing-modal__form-label">REGON</label>
                            <input
                                type="text"
                                name="regon"
                                value={invoiceFormData.regon || ''}
                                onChange={onInvoiceChange}
                                placeholder="000000000"
                                className="billing-modal__form-input"
                            />
                        </div>
                    </div>

                    {/* Address */}
                    <div className="billing-modal__form-group">
                        <label className="billing-modal__form-label">{t('billing.street')} *</label>
                        <input
                            type="text"
                            name="street"
                            value={invoiceFormData.street || ''}
                            onChange={onInvoiceChange}
                            placeholder={t('billing.street')}
                            className="billing-modal__form-input"
                        />
                    </div>

                    <div className="billing-modal__form-row billing-modal__form-row--3">
                        <div className="billing-modal__form-group">
                            <label className="billing-modal__form-label">{t('billing.buildingNumber')}</label>
                            <input
                                type="text"
                                name="buildingNumber"
                                value={invoiceFormData.buildingNumber || ''}
                                onChange={onInvoiceChange}
                                placeholder="Nr"
                                className="billing-modal__form-input"
                            />
                        </div>
                        <div className="billing-modal__form-group">
                            <label className="billing-modal__form-label">{t('billing.apartmentNumber')}</label>
                            <input
                                type="text"
                                name="apartmentNumber"
                                value={invoiceFormData.apartmentNumber || ''}
                                onChange={onInvoiceChange}
                                placeholder="Nr"
                                className="billing-modal__form-input"
                            />
                        </div>
                        <div className="billing-modal__form-group">
                            <label className="billing-modal__form-label">{t('billing.postalCode')} *</label>
                            <input
                                type="text"
                                name="postalCode"
                                value={invoiceFormData.postalCode || ''}
                                onChange={onInvoiceChange}
                                placeholder="00-000"
                                className="billing-modal__form-input"
                            />
                        </div>
                    </div>

                    <div className="billing-modal__form-row">
                        <div className="billing-modal__form-group">
                            <label className="billing-modal__form-label">{t('billing.city')} *</label>
                            <input
                                type="text"
                                name="city"
                                value={invoiceFormData.city || ''}
                                onChange={onInvoiceChange}
                                placeholder={t('billing.city')}
                                className="billing-modal__form-input"
                            />
                        </div>
                        <div className="billing-modal__form-group">
                            <label className="billing-modal__form-label">{t('billing.country')} *</label>
                            <input
                                type="text"
                                name="country"
                                value={invoiceFormData.country || ''}
                                onChange={onInvoiceChange}
                                placeholder={t('billing.country')}
                                className="billing-modal__form-input"
                            />
                        </div>
                    </div>
                </div>

                <div className="billing-modal__form-actions">
                    {invoiceData && (
                        <button
                            className="billing-modal__btn billing-modal__btn--text"
                            onClick={onCancel}
                        >
                            {t('common:cancel')}
                        </button>
                    )}
                    <button
                        className="billing-modal__btn billing-modal__btn--primary"
                        onClick={onSave}
                    >
                        {t('billing.saveData')}
                    </button>
                </div>
            </div>
        );
    }

    // Data exists - show summary
    return (
        <div className="billing-modal__section">
            <h3 className="billing-modal__section-title">{t('billing.invoiceDataTitle')}</h3>
            <p className="billing-modal__section-desc">
                {t('billing.invoiceDataDesc')}
            </p>

            <div className="billing-modal__data-card">
                <div className="billing-modal__data-row">
                    <span className="billing-modal__data-label">{t('billing.companyName')}</span>
                    <span className="billing-modal__data-value">{invoiceData.companyName}</span>
                </div>
                {invoiceData.nip && (
                    <div className="billing-modal__data-row">
                        <span className="billing-modal__data-label">NIP</span>
                        <span className="billing-modal__data-value">{invoiceData.nip}</span>
                    </div>
                )}
                {invoiceData.regon && (
                    <div className="billing-modal__data-row">
                        <span className="billing-modal__data-label">REGON</span>
                        <span className="billing-modal__data-value">{invoiceData.regon}</span>
                    </div>
                )}
                <div className="billing-modal__data-row">
                    <span className="billing-modal__data-label">{t('billing.address')}</span>
                    <span className="billing-modal__data-value">
                        {formatAddress(invoiceData)}
                    </span>
                </div>
            </div>

            <button
                className="billing-modal__btn billing-modal__btn--text billing-modal__edit-btn"
                onClick={onEdit}
            >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M14.06 9.02l.92.92L5.92 19H5v-.92l9.06-9.06M17.66 3c-.25 0-.51.1-.7.29l-1.83 1.83 3.75 3.75 1.83-1.83c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.2-.2-.45-.29-.71-.29zm-3.6 3.19L3 17.25V21h3.75L17.81 9.94l-3.75-3.75z"/>
                </svg>
                {t('billing.editData')}
            </button>
        </div>
    );
};

export default BillingPreferenceModal;
